import {
  GroupNamesOptionNames,
  GroupNamesOptionNamesIntl,
  SpoIntlType,
  SpoType,
  StockInputValue_Edit_NoOption,
  StockInputValue_Edit_NoOption_Intl,
  StockInputValue_Edit_Option,
  StockInputValue_Edit_Option_Intl,
  StockInputValue_New_NoOption,
  StockInputValue_New_Option,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_Stock/types.ts';
import { Lang } from '../../../../../../../../shared/type';
import {
  PureInput,
  PureInputType,
  pureInputVariant,
} from '../../../../../../../../shared/rune-component/Input/PureInput.ts';
import {
  NumberTextInput,
  NumberTextInputType,
} from '../../../../../../../../shared/rune-component/Input/NumberTextInput.ts';
import { SPO_ITEM_STOCK__MAX } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/constants.ts';
import { StudioButton } from '@services/MarppleShop/shared/components/atoms/StudioButton/StudioButton.ts';
import {
  StockVolumeModal,
  VolumeInputType,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_Stock/views/StockVolumeModal/StockVolumeModal.ts';
import { html } from 'rune-ts';
import klass from './Input_Stock.module.scss';
import { every, pipe, values } from '@fxts/core';
import { MODE } from '@services/MarppleShopStudio/widget/CreatorProduct/core/types/types.ts';

export const stockDataUtil = {
  formatOptionNames(groupNamesOptionNames: GroupNamesOptionNames[] | null): string {
    if (!groupNamesOptionNames) return '';
    return groupNamesOptionNames.map((item) => item.option_name).join(' / ');
  },

  formatOptionNamesIntl(
    groupNamesOptionNames: GroupNamesOptionNamesIntl[] | null,
    lang: Extract<Lang, typeof Lang.en | typeof Lang.jp>,
  ): string {
    if (!groupNamesOptionNames) return '';
    return groupNamesOptionNames
      .map((item) => {
        return lang === Lang.en ? item.option_name_en : item.option_name_jp;
      })
      .join(' / ');
  },

  isSpoIntlType(mode: MODE, spo: SpoType | SpoIntlType): spo is SpoIntlType {
    return spo && mode === MODE.EDIT_TPL_INTL;
  },

  formatName(sku?: string): string {
    return sku || '';
  },

  toNewOptionData(spo: SpoType | SpoIntlType): StockInputValue_New_Option[] {
    return spo._.spo_items.map((item) => ({
      name: this.formatOptionNames(item.group_names_option_names),
      price: item.price,
      stock: item?._?.spo_item_stock?.quantity,
      sku: item?._?.sku?.nickname,
    }));
  },

  toNewNoOptionData(spo: SpoType | SpoIntlType): StockInputValue_New_NoOption[] {
    const firstItem = spo._.spo_items[0];
    return [
      {
        stock: firstItem?._?.spo_item_stock?.quantity || 0,
        sku: firstItem?._.sku?.nickname,
      },
    ];
  },

  toEditNoOptionData(spo: SpoType | SpoIntlType): StockInputValue_Edit_NoOption[] {
    const firstItem = spo._.spo_items[0];
    return [
      {
        stock: firstItem?._?.spo_item_stock?.quantity || 0,
        sold_quantity: firstItem?.sold_quantity || 0,
        sku: firstItem?._?.sku?.nickname,
        id: firstItem?._?.sku?.id,
      },
    ];
  },

  toEditNoOptionIntlData(spo: SpoIntlType): StockInputValue_Edit_NoOption_Intl[] {
    const firstItem = spo._.spo_items[0];
    return [
      {
        stock: firstItem?._?.spo_item_stock?.quantity || 0,
        sold_quantity: firstItem?.sold_quantity || 0,
        sku: firstItem?._?.sku?.nickname,
        id: firstItem?._?.sku?.id,
        volume: {
          width: firstItem?._?.sku?.width || undefined,
          height: firstItem?._?.sku?.height || undefined,
          length: firstItem?._?.sku?.length || undefined,
          weight: firstItem?._?.sku?.weight || undefined,
        },
      },
    ];
  },

  toEditOptionData(spo: SpoType): StockInputValue_Edit_Option[] {
    return spo._.spo_items.map((item) => ({
      name: this.formatOptionNames(item.group_names_option_names),
      price: item.price,
      stock: item?._?.spo_item_stock?.quantity,
      sold_quantity: item.sold_quantity,
      sku: item?._?.sku?.nickname,
      id: item?._?.sku?.id,
    }));
  },

  toEditOptionIntlData(spo: SpoIntlType): StockInputValue_Edit_Option_Intl[] {
    return spo._.spo_items.map((item) => ({
      name: this.formatOptionNames(item.group_names_option_names),
      name_en: this.formatOptionNamesIntl(item.group_names_option_names, Lang.en),
      name_jp: this.formatOptionNamesIntl(item.group_names_option_names, Lang.jp),
      price: item.price,
      stock: item?._?.spo_item_stock?.quantity,
      sold_quantity: item.sold_quantity,
      sku: item?._?.sku?.nickname,
      id: item?._?.sku?.id,
      volume: {
        width: item?._?.sku?.width,
        height: item?._?.sku?.height,
        length: item?._?.sku?.length,
        weight: item?._?.sku?.weight,
      },
    }));
  },

  textInput({
    index,
    name,
    value,
    maxlength = 50,
    onFocusout,
    placeholder,
    readonly,
  }: {
    index: number;
    name: string;
    value?: string;
    placeholder?: string;
    maxlength?: number;
    readonly?: boolean;
    onFocusout?: (view: PureInputType) => void;
  }) {
    return new PureInput({
      variant: pureInputVariant.studio_editor_stock_option,
      index,
      type: 'text',
      name,
      value,
      maxlength,
      onFocusout,
      placeholder,
      readonly,
    });
  },

  numberInput({
    index,
    name,
    value = 0,
    min = 0,
    max,
    placeholder,
    onFocusout,
    readonly,
  }: {
    index: number;
    name: string;
    value?: number;
    placeholder?: string;
    maxlength?: number;
    readonly?: boolean;
    min?: number;
    max?: number;
    onFocusout?: (view: NumberTextInputType) => void;
  }) {
    return new NumberTextInput({
      variant: pureInputVariant.studio_editor_stock_option,
      index,
      name,
      value,
      placeholder,
      min,
      max,
      onFocusout,
      readonly,
    });
  },

  volumeButton({
    index,
    name,
    value,
    onRegister,
    readonly,
    min = 0,
    max = SPO_ITEM_STOCK__MAX,
  }: {
    index: number;
    name: string;
    value: StockInputValue_Edit_Option_Intl['volume'];
    min?: number;
    max?: number;
    readonly?: boolean;
    onRegister?: (view: VolumeInputType) => void;
  }) {
    const is_new = pipe(
      values(value),
      every((volume) => !volume),
    );

    const stockVolumeModal = StockVolumeModal.create({
      index,
      name,
      value,
      min,
      max,
      onRegister,
    });
    const stockVolumeButton = new StudioButton({
      label: is_new ? '등록' : '수정',
      color: is_new ? 'line_sky' : 'line_gray',
      size: 'free',
      name: 'width',
      custom_klass: klass.stock_volume_button,
      is_disabled: readonly,
      data_attr: {
        is_registered: String(!is_new),
      },
      onClick: () => stockVolumeModal.open(),
      index,
    });

    return html`<div>${stockVolumeButton} ${stockVolumeModal}</div>`;
  },
};
